import React, { FC, useMemo } from "react";
import { Link } from "react-router-dom";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetPortalReportDataQuery } from "../../../slices/ReportDataSlice";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable } from "../ask/reportAsk.utils";
import {
  PORTAL_ORDER,
  PORTAL_STATUS_TEXT,
  portalLine,
  portalStats,
  shortDate,
} from "../moreReports.utils";
import { useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import { ChoiceMenu } from "../ui/ReportFilters";
import {
  Card,
  Empty,
  Kpi,
  KpiGrid,
  LINK,
  LoadError,
  Loading,
  Note,
  ReportPage,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";

const PILL = {
  active: "bg-[#E9F9F1] text-[#147A4B]",
  invited: "bg-[#FFF4E5] text-[#8A4B00]",
  expired: "bg-[#FFF4E5] text-[#8A4B00]",
  none: "bg-[#F4F5F8] text-[#5D6475]",
} as const;
type Show = "all" | "active" | "invited" | "none";
const SHOW: { value: Show; label: string }[] = [
  { value: "all", label: "All clients" },
  { value: "active", label: "Active" },
  { value: "invited", label: "Invited, not set up" },
  { value: "none", label: "Not invited" },
];
const PORTAL_TABLE = "Portal by client";
const SIGN_TABLE = "Signatures still waiting";

/** Client portal and signatures (2026-11-05): portal use and signatures still waiting. */
export const PortalReport: FC = () => {
  const access = useReportsAccess();
  const orgId = getLinkedFirm()?.orgId || "";
  const [show, setShow] = useViewExtra<Show>("show", "all", SHOW, "Show");
  const data = useGetPortalReportDataQuery({ orgId }, { skip: !orgId });
  const report = data.data;
  const stats = report ? portalStats(report) : null;
  const clients = useMemo(
    () =>
      [...(report?.clients ?? [])]
        .filter((c) =>
          show === "all"
            ? true
            : show === "invited"
            ? c.portal === "invited" || c.portal === "expired"
            : c.portal === show,
        )
        .sort(
          (a, b) =>
            PORTAL_ORDER[a.portal] - PORTAL_ORDER[b.portal] ||
            a.name.localeCompare(b.name),
        ),
    [report, show],
  );
  const waiting = useMemo(() => report?.waiting ?? [], [report]);
  const v = (x: React.ReactNode) => (data.isLoading ? "…" : x);
  const shownClients = useFocusRows(PORTAL_TABLE, clients, (c) => c.customerId);
  const clientPage = usePagedRows(shownClients);
  const shownSigs = useFocusRows(SIGN_TABLE, waiting, (s) => s.id);
  const sigPage = usePagedRows(shownSigs);

  const onExport = () =>
    exportDataToExcel(
      (report?.clients ?? []).map((c) => ({
        Client: c.name,
        Portal: PORTAL_STATUS_TEXT[c.portal],
        "Uploads (last 90 days)": c.uploadsLast90Days,
        "Last upload": c.lastUploadAt
          ? shortDate(new Date(c.lastUploadAt))
          : "",
        "Signatures waiting": waiting.filter(
          (w) => w.client.customerId === c.customerId,
        ).length,
      })),
      "Client portal and signatures",
    );

  const ask = useMemo(
    () => ({
      title: "Client portal and signatures",
      path: "/reports/portal",
      description:
        "Which clients use the client portal, client uploads in the last 90 days, and signatures still waiting.",
      kpis: stats
        ? [
            {
              label: "Clients using the portal",
              value: `${stats.active} of ${stats.total}`,
            },
            { label: "Invited, not set up", value: String(stats.invited) },
            {
              label: "Files uploaded by clients (90 days)",
              value: String(stats.uploads),
            },
            { label: "Signatures waiting", value: String(stats.waiting) },
          ]
        : [],
      tables: [
        askTable(
          SIGN_TABLE,
          waiting,
          [
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (s) => s.client.name,
            },
            {
              key: "document",
              label: "Document",
              kind: "text",
              value: (s) => s.document,
            },
            {
              key: "days",
              label: "Days waiting",
              kind: "number",
              value: (s) => s.daysWaiting,
            },
          ],
          (s) => s.id,
          (s) => s.client.link,
        ),
        askTable(
          PORTAL_TABLE,
          report?.clients ?? [],
          [
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (c) => c.name,
            },
            {
              key: "portal",
              label: "Portal",
              kind: "text",
              value: (c) => PORTAL_STATUS_TEXT[c.portal],
            },
            {
              key: "uploads",
              label: "Uploads (90 days)",
              kind: "number",
              value: (c) => c.uploadsLast90Days,
            },
          ],
          (c) => c.customerId,
          (c) => c.link,
        ),
      ],
    }),
    [report, stats, waiting],
  );

  return (
    <ReportPage
      title={"Client portal and signatures"}
      description={
        "Which clients use the portal, and which documents are still waiting for a signature."
      }
      onExport={report?.clients.length ? onExport : undefined}
      ask={data.isLoading ? undefined : ask}
      askSuggestions={[
        "Who should I invite to the portal?",
        "Which signatures are the oldest?",
        "How many clients uploaded files lately?",
      ]}
      filters={
        <ChoiceMenu
          label={"Show"}
          options={SHOW}
          value={show}
          onChange={setShow}
        />
      }>
      {data.isError && <LoadError />}
      {report && !report.available && (
        <Note>The client portal needs a database update first.</Note>
      )}
      <KpiGrid>
        <Kpi
          label={"Clients using the portal"}
          value={v(stats ? `${stats.active} of ${stats.total}` : "—")}
          note={
            stats
              ? `${stats.pct}% · ${stats.invited} invited, not set up yet`
              : undefined
          }
        />
        <Kpi
          label={"Files uploaded by clients"}
          value={v(stats?.uploads ?? 0)}
          note={"last 90 days"}
        />
        <Kpi
          label={"Signatures waiting"}
          value={v(stats?.waiting ?? 0)}
          note={
            stats ? `${stats.waitingLong} sent over 14 days ago` : undefined
          }
          tone={stats?.waitingLong ? "red" : "default"}
        />
        <Kpi
          label={"Average time to sign"}
          value={v(
            report?.averageDaysToSign == null
              ? "—"
              : `${report.averageDaysToSign} days`,
          )}
          note={"signed this year"}
        />
      </KpiGrid>

      <div className={"grid items-start gap-[18px] lg:grid-cols-2"}>
        <Card title={SIGN_TABLE}>
          {data.isLoading ? (
            <Loading />
          ) : !waiting.length ? (
            <Empty
              text={
                report && !report.signaturesAvailable
                  ? "Signatures need a database update first."
                  : "Nothing is waiting for a signature."
              }
            />
          ) : (
            <TableBox pager={sigPage.pager}>
              <thead>
                <tr>
                  <th scope={"col"} className={TH}>
                    Client
                  </th>
                  <th scope={"col"} className={TH}>
                    Document
                  </th>
                  <th scope={"col"} className={TH}>
                    Waiting
                  </th>
                </tr>
              </thead>
              <tbody>
                {sigPage.pageRows.map((s) => (
                  <tr key={s.id}>
                    <td className={TD}>
                      {s.client.link ? (
                        <Link to={s.client.link} className={LINK}>
                          {s.client.name}
                        </Link>
                      ) : (
                        s.client.name
                      )}
                    </td>
                    <td
                      className={`${TD} max-w-[220px] truncate text-[#3F4757]`}>
                      {s.document}
                    </td>
                    <td
                      className={`${TD} font-bold ${
                        s.daysWaiting >= 14
                          ? "text-[#B42318]"
                          : "text-[#3A4256]"
                      }`}>
                      {s.daysWaiting} day{s.daysWaiting === 1 ? "" : "s"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableBox>
          )}
        </Card>
        <Card title={PORTAL_TABLE}>
          {data.isLoading ? (
            <Loading />
          ) : !clients.length ? (
            <Empty text={"No clients to show."} />
          ) : (
            <TableBox pager={clientPage.pager}>
              <thead>
                <tr>
                  <th scope={"col"} className={TH}>
                    Client
                  </th>
                  <th scope={"col"} className={TH}>
                    Portal
                  </th>
                  <th scope={"col"} className={TH}>
                    Latest
                  </th>
                </tr>
              </thead>
              <tbody>
                {clientPage.pageRows.map((c) => (
                  <tr key={c.customerId}>
                    <td className={TD}>
                      {c.link ? (
                        <Link to={c.link} className={LINK}>
                          {c.name}
                        </Link>
                      ) : (
                        c.name
                      )}
                    </td>
                    <td className={TD}>
                      <span
                        className={`rounded-full px-2.5 py-[3px] text-[12.5px] font-bold ${
                          PILL[c.portal]
                        }`}>
                        {PORTAL_STATUS_TEXT[c.portal]}
                      </span>
                    </td>
                    <td
                      className={`${TD} max-w-[240px] truncate text-[12.5px] text-[#5B6472]`}>
                      {portalLine(c)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableBox>
          )}
        </Card>
      </div>

      <Note>
        Red = sent 14 or more days ago. Invite people, share files and send
        documents to sign from the client&apos;s &quot;Client portal&quot; tab.
        {!access.firmWide && " You see your own clients."}
      </Note>
    </ReportPage>
  );
};
